import { Type, type Static } from "@sinclair/typebox";
import { CancelledBy, Cents, DriverStatus, LatLng, PaymentMethod, Place, PlaceInput, RideCategory, TripKind, TripStatus } from "./domain.ts";
import { DriverTripTypeView, Gender } from "./trip-types.ts";

/** REST DTOs for the driver app (Motorista-01…16). */

export const DriverReviewStatus = Type.Union([Type.Literal("draft"), Type.Literal("pending_review"), Type.Literal("resubmission_requested"), Type.Literal("approved"), Type.Literal("rejected"), Type.Literal("suspended")], { $id: "DriverReviewStatus" });
export type DriverReviewStatus = Static<typeof DriverReviewStatus>;

export const DOCUMENT_KINDS = ["cnh_front", "cnh_back", "crlv", "background_state", "background_federal", "profile_photo"] as const;
export const DocumentKind = Type.Union([Type.Literal("cnh_front"), Type.Literal("cnh_back"), Type.Literal("crlv"), Type.Literal("background_state"), Type.Literal("background_federal"), Type.Literal("profile_photo")], { $id: "DocumentKind" });
export type DocumentKind = Static<typeof DocumentKind>;
/** pt-BR names drivers read in pushes and GO refusals ("Reenvie sua CNH · frente"). */
export const DOCUMENT_NAMES: Record<(typeof DOCUMENT_KINDS)[number] | "liveness", string> = {
  cnh_front: "CNH · frente", cnh_back: "CNH · verso", crlv: "CRLV-e", background_state: "antecedentes estadual",
  background_federal: "antecedentes federal", profile_photo: "foto de perfil", liveness: "prova de vida",
};
export const DocumentStatus = Type.Union([Type.Literal("pending"), Type.Literal("approved"), Type.Literal("rejected"), Type.Literal("expired")], { $id: "DocumentStatus" });

/**
 * Prova de vida: a short front-camera video (look at the camera, turn left, turn right) that staff
 * compare with the CNH before the first GO. `required` until one is sent; `rejected` asks for a new one.
 */
export const LivenessStatus = Type.Union([Type.Literal("required"), Type.Literal("pending"), Type.Literal("approved"), Type.Literal("rejected")], { $id: "LivenessStatus" });
export type LivenessStatus = Static<typeof LivenessStatus>;
export const LivenessView = Type.Object(
  {
    status: LivenessStatus,
    /** Why staff asked for a new video. */
    rejectionReason: Type.Union([Type.String(), Type.Null()]),
    submittedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "LivenessView" },
);
export type LivenessView = Static<typeof LivenessView>;
/** What the app measured while recording; staff see it next to the video ("Sinais do app"). */
export const LivenessMeta = Type.Object(
  {
    durationMs: Type.Integer({ minimum: 0, maximum: 120_000 }),
    /** When each step finished, ms from the start: frente, esquerda, direita. */
    stepsMs: Type.Object({ front: Type.Integer({ minimum: 0 }), left: Type.Integer({ minimum: 0 }), right: Type.Integer({ minimum: 0 }) }),
    /** The furthest the head turned, in degrees (positive both ways). */
    peakYawLeft: Type.Number({ minimum: 0, maximum: 90 }),
    peakYawRight: Type.Number({ minimum: 0, maximum: 90 }),
    /** Frames where more than one face showed (0 for a clean take). */
    multipleFaceFrames: Type.Integer({ minimum: 0 }),
    device: Type.String({ maxLength: 80 }),
    platform: Type.Union([Type.Literal("ios"), Type.Literal("android")]),
  },
  { $id: "LivenessMeta" },
);
export type LivenessMeta = Static<typeof LivenessMeta>;

export const PixKeyType = Type.Union([Type.Literal("cpf"), Type.Literal("phone"), Type.Literal("email"), Type.Literal("random")], { $id: "PixKeyType" });
export type PixKeyType = Static<typeof PixKeyType>;

const Day = Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$", description: "YYYY-MM-DD" });
const Month = Type.String({ pattern: "^\\d{4}-\\d{2}$", description: "YYYY-MM" });

// ── Onboarding (Motorista-01, 09, 02, 10, 11) ────────────────────────────────────────────

export const DriverPersonalInput = Type.Object(
  {
    fullName: Type.String({ minLength: 5, maxLength: 120 }),
    cpf: Type.String({ minLength: 11, maxLength: 14 }),
    birthDate: Day,
    cnhNumber: Type.String({ pattern: "^\\d{11}$" }),
    cnhCategory: Type.Union([Type.Literal("A"), Type.Literal("B"), Type.Literal("AB"), Type.Literal("C"), Type.Literal("D"), Type.Literal("E")]),
    /** The CNH shows only month and year; stored as the month's last day. */
    cnhExpiresAt: Month,
    cnhHasEar: Type.Boolean(),
    /** Optional; staff confirm it against the CNH. Unlocks types limited by gender. */
    gender: Type.Optional(Type.Union([Gender, Type.Null()])),
  },
  { $id: "DriverPersonalInput" },
);
export type DriverPersonalInput = Static<typeof DriverPersonalInput>;

export const DriverAddressInput = Type.Object(
  {
    cityId: Type.String(),
    cep: Type.String({ pattern: "^\\d{5}-?\\d{3}$" }),
    line: Type.String({ minLength: 3, maxLength: 200, description: "Street · bairro, as filled from the CEP" }),
    number: Type.String({ minLength: 1, maxLength: 12 }),
    complement: Type.Optional(Type.Union([Type.String({ maxLength: 80 }), Type.Null()])),
  },
  { $id: "DriverAddressInput" },
);
export type DriverAddressInput = Static<typeof DriverAddressInput>;

export const VehicleLookup = Type.Object(
  { plate: Type.String(), make: Type.String(), model: Type.String(), color: Type.String(), year: Type.Union([Type.Integer(), Type.Null()]), isMotorcycle: Type.Boolean() },
  { $id: "VehicleLookup" },
);
export const DriverVehicleInput = Type.Object(
  {
    plate: Type.String({ pattern: "^[A-Za-z]{3}-?\\d[A-Za-z0-9]\\d{2}$", description: "Mercosul (ABC1D23) or old (ABC-1234)" }),
    renavam: Type.String({ pattern: "^\\d{9,11}$" }),
    year: Type.Integer({ minimum: 1990, maximum: 2100 }),
    make: Type.String({ minLength: 2, maxLength: 40 }),
    model: Type.String({ minLength: 1, maxLength: 60 }),
    color: Type.String({ minLength: 3, maxLength: 30 }),
    isMotorcycle: Type.Optional(Type.Boolean()),
  },
  { $id: "DriverVehicleInput" },
);
export type DriverVehicleInput = Static<typeof DriverVehicleInput>;

export const DriverPayoutInput = Type.Object(
  { pixKeyType: PixKeyType, pixKey: Type.String({ minLength: 3, maxLength: 77 }), acceptTerms: Type.Literal(true) },
  { $id: "DriverPayoutInput" },
);
export type DriverPayoutInput = Static<typeof DriverPayoutInput>;

export const DriverDocumentView = Type.Object(
  {
    kind: DocumentKind,
    status: DocumentStatus,
    rejectionReason: Type.Union([Type.String(), Type.Null()]),
    expiresAt: Type.Union([Day, Type.Null()]),
    uploadedAt: Type.String({ format: "date-time" }),
    /** Staff asked for it again and GO stays locked until a new one is approved. */
    blocksGo: Type.Optional(Type.Boolean()),
    /** When staff decided on it (for a rejected one: when they asked for it again). */
    reviewedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
  },
  { $id: "DriverDocumentView" },
);

export const DriverVehicleView = Type.Object(
  {
    plate: Type.String(),
    make: Type.String(),
    model: Type.String(),
    year: Type.Integer(),
    color: Type.String(),
    isMotorcycle: Type.Boolean(),
    /** Types this vehicle can drive now (rules + staff grants), ids in display order. */
    eligibleCategories: Type.Array(RideCategory),
  },
  { $id: "DriverVehicleView" },
);

/** Which onboarding steps are filled; the app resumes at the first missing one. */
export const OnboardingSteps = Type.Object(
  { personal: Type.Boolean(), address: Type.Boolean(), vehicle: Type.Boolean(), documents: Type.Boolean(), payout: Type.Boolean() },
  { $id: "OnboardingSteps" },
);

/**
 * "Filtro de destino" (Motorista-15): while active, offers only trips whose dropoff brings the
 * driver at least 30 % closer to `place` (straight line). Two activations per local day.
 */
export const DestinationFilterView = Type.Object(
  { active: Type.Boolean(), place: Type.Union([Place, Type.Null()]), usesLeftToday: Type.Integer() },
  { $id: "DestinationFilterView" },
);
export type DestinationFilterView = Static<typeof DestinationFilterView>;
export const DestinationFilterInput = Type.Object({ place: PlaceInput }, { $id: "DestinationFilterInput" });
export const DESTINATION_FILTER_DAILY_USES = 2;

/**
 * Everything the driver app shows about the account: onboarding progress (Motorista-01…11) and
 * Conta e veículo (Motorista-15). `status` is null before the first onboarding step is saved.
 */
export const DriverProfileView = Type.Object(
  {
    status: Type.Union([DriverReviewStatus, Type.Null()]),
    rejectionReason: Type.Union([Type.String(), Type.Null()]),
    steps: OnboardingSteps,
    personal: Type.Union([
      Type.Object({
        fullName: Type.String(),
        cpfMasked: Type.String(),
        birthDate: Day,
        cnhNumber: Type.String(),
        cnhCategory: Type.String(),
        cnhExpiresAt: Day,
        cnhHasEar: Type.Boolean(),
        gender: Type.Union([Gender, Type.Null()]),
        /** Staff checked the gender against the CNH (gender rules use only a confirmed one). */
        genderConfirmed: Type.Boolean(),
      }),
      Type.Null(),
    ]),
    address: Type.Union([
      Type.Object({ cityId: Type.String(), cep: Type.String(), line: Type.String(), number: Type.String(), complement: Type.Union([Type.String(), Type.Null()]) }),
      Type.Null(),
    ]),
    vehicle: Type.Union([DriverVehicleView, Type.Null()]),
    documents: Type.Array(DriverDocumentView),
    /** Documents still to upload before the cadastro can be sent. */
    missingDocuments: Type.Array(DocumentKind),
    payout: Type.Union([Type.Object({ pixKeyType: PixKeyType, pixKeyMasked: Type.String(), verified: Type.Boolean() }), Type.Null()]),
    termsAccepted: Type.Boolean(),
    rating: Type.Number(),
    completedTrips: Type.Integer(),
    /** Conta → Tipos de corrida: every active type, eligible ones first, with the driver's switch. */
    tripTypes: Type.Array(DriverTripTypeView),
    /** "Aceito pets": offers for rides with Levar pet. Off by default. */
    acceptsPets: Type.Boolean(),
    /** Segurança → Gravar áudio das corridas. */
    recordAudio: Type.Boolean(),
    /** "Aceitar pagamento na hora": offers for cash trips. */
    acceptsCash: Type.Boolean(),
    destinationFilter: DestinationFilterView,
    /** The avatar: the latest `profile_photo` upload (a short-lived link, ADR 0012), null before one. */
    photoUrl: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    /** Prova de vida, once the cadastro is approved (null before). Not in `documents`, which older apps decode strictly. */
    liveness: Type.Optional(Type.Union([LivenessView, Type.Null()])),
  },
  { $id: "DriverProfileView" },
);
export type DriverProfileView = Static<typeof DriverProfileView>;

export const DriverPreferencesInput = Type.Object(
  {
    /** Switch types on or off (only those with `canToggle`). */
    tripTypes: Type.Optional(Type.Record(Type.String(), Type.Boolean())),
    acceptsPets: Type.Optional(Type.Boolean()),
    recordAudio: Type.Optional(Type.Boolean()),
    acceptsCash: Type.Optional(Type.Boolean()),
  },
  { $id: "DriverPreferencesInput" },
);

export const CityView = Type.Object({ id: Type.String(), name: Type.String(), state: Type.String() }, { $id: "CityView" });

// ── Online (Motorista-03) and earnings (Motorista-06) ────────────────────────────────────

export const DriverHomeView = Type.Object(
  {
    presence: DriverStatus,
    /** Categories offered to the driver while online. */
    categories: Type.Array(RideCategory),
    /** The same types with their names, in display order. */
    tripTypes: Type.Array(Type.Object({ id: RideCategory, name: Type.String() })),
    today: Type.Object({ earningsCents: Cents, trips: Type.Integer(), onlineSeconds: Type.Integer() }),
    rating: Type.Number(),
    firstName: Type.String(),
  },
  { $id: "DriverHomeView" },
);
export type DriverHomeView = Static<typeof DriverHomeView>;

export const EarningsPeriod = Type.Union([Type.Literal("day"), Type.Literal("week"), Type.Literal("month")], { $id: "EarningsPeriod" });
export type EarningsPeriod = Static<typeof EarningsPeriod>;

export const DriverEarningsView = Type.Object(
  {
    period: EarningsPeriod,
    /** First and last calendar day (city time zone), inclusive. */
    from: Day,
    to: Day,
    totalCents: Cents,
    trips: Type.Integer(),
    onlineSeconds: Type.Integer(),
    /** One bar per day of the period (one for "day"). */
    days: Type.Array(Type.Object({ date: Day, cents: Cents })),
    /** `collectedCents`: cash taken from riders in the period (Pagar na hora); it comes out of the repasse. */
    breakdown: Type.Object({ tripsCents: Cents, cancellationFeesCents: Cents, tipsCents: Cents, promotionsCents: Cents, collectedCents: Type.Optional(Cents) }),
    /**
     * `balanceCents`: the whole repasse balance, negative when cash commissions are owed ("Quitar
     * saldo"). `cashBlocked`: at or below the limit, so no more cash offers until it recovers.
     */
    balance: Type.Object({
      availableCents: Cents, nextPayoutAt: Type.String({ format: "date-time" }),
      balanceCents: Type.Optional(Type.Integer()), cashBlocked: Type.Optional(Type.Boolean()),
    }),
    payout: Type.Union([Type.Object({ pixKeyType: PixKeyType, pixKeyMasked: Type.String() }), Type.Null()]),
  },
  { $id: "DriverEarningsView" },
);
export type DriverEarningsView = Static<typeof DriverEarningsView>;

/** Atividade (driver): one trip the driver took. Money is only ever the driver's own; the rider's fare never shows. */
export const DriverTripHistoryItem = Type.Object(
  {
    id: Type.String(),
    code: Type.String(),
    status: TripStatus,
    kind: TripKind,
    category: RideCategory,
    /** The type's name ("Conforto"). */
    categoryName: Type.String(),
    pickupAddress: Type.String(),
    dropoffAddress: Type.String(),
    /** What the driver was credited for the trip (their share, or the no-show / cancellation fee), tips apart. */
    earnedCents: Cents,
    /** A paid tip (0 without one). */
    tipCents: Cents,
    /** Cash the driver took from the rider (Pagar na hora), which comes out of the repasse; 0 otherwise. */
    collectedCents: Type.Optional(Cents),
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
    requestedAt: Type.String({ format: "date-time" }),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    cancelledBy: Type.Union([CancelledBy, Type.Null()]),
    riderFirstName: Type.Union([Type.String(), Type.Null()]),
    paymentMethod: PaymentMethod,
  },
  { $id: "DriverTripHistoryItem" },
);
export const DriverTripHistory = Type.Object(
  {
    items: Type.Array(DriverTripHistoryItem),
    nextCursor: Type.Union([Type.String(), Type.Null()]),
    /** This calendar month (city time zone): everything credited (trips, fees, tips) and the completed trips. */
    month: Type.Object({ startsAt: Type.String({ format: "date-time" }), totalCents: Cents, trips: Type.Integer() }),
  },
  { $id: "DriverTripHistory" },
);
export type DriverTripHistory = Static<typeof DriverTripHistory>;

/** Surge levels, lowest first: the automatic surge's steps (`surge.auto_*`), and the driver map's shades. */
export const SURGE_LEVELS = ["low", "medium", "high", "peak"] as const;
export const SurgeLevel = Type.Union([Type.Literal("low"), Type.Literal("medium"), Type.Literal("high"), Type.Literal("peak")], { $id: "SurgeLevel" });
export type SurgeLevel = Static<typeof SurgeLevel>;

/**
 * Mapa de ganhos (driver Home): the H3 res-7 zones around the driver that pay more right now.
 * Money is only ever what the driver keeps per km (after commission); the rider's fare never shows.
 */
export const DriverZonesView = Type.Object(
  {
    /** `driver_map.enabled`; off, `zones` is empty and the apps hide the layer. */
    enabled: Type.Boolean(),
    at: Type.String({ format: "date-time" }),
    /** Poll again after this many seconds while Home is on screen (`driver_map.refresh_seconds`). */
    refreshAfterSeconds: Type.Integer(),
    /** The driver's trip types in display order, with what they keep per km outside a boosted zone. */
    tripTypes: Type.Array(Type.Object({ id: RideCategory, name: Type.String(), perKmCents: Cents })),
    /** Highest multiplier first. */
    zones: Type.Array(
      Type.Object({
        /** H3 res-7 cell. */
        cell: Type.String(),
        center: LatLng,
        /** The hexagon's corners, to draw it without an H3 library. */
        boundary: Type.Array(LatLng),
        multiplier: Type.Number(),
        level: SurgeLevel,
        /** When the zone ends as it stands now; null when staff set it with no end. It may be extended. */
        activeUntil: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
        /** Per km in this zone, one per `tripTypes` entry, in the same order. */
        rates: Type.Array(Type.Object({ tripType: RideCategory, perKmCents: Cents })),
      }),
    ),
  },
  { $id: "DriverZonesView" },
);
export type DriverZonesView = Static<typeof DriverZonesView>;

// ── Navigation (Motorista-05, 13) ────────────────────────────────────────────────────────

export const Maneuver = Type.Union([Type.Literal("straight"), Type.Literal("turn_left"), Type.Literal("turn_right"), Type.Literal("slight_left"), Type.Literal("slight_right"), Type.Literal("sharp_left"), Type.Literal("sharp_right"), Type.Literal("uturn"), Type.Literal("roundabout"), Type.Literal("merge"), Type.Literal("ramp"), Type.Literal("fork"), Type.Literal("depart"), Type.Literal("arrive")], { $id: "Maneuver" });
export type Maneuver = Static<typeof Maneuver>;

export const NavigationStep = Type.Object(
  {
    /** pt-BR, e.g. "Vire à direita na R. Augusta". */
    instruction: Type.String(),
    maneuver: Maneuver,
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
    start: LatLng,
    end: LatLng,
  },
  { $id: "NavigationStep" },
);

/** A stretch of the route polyline in traffic: points `start`…`end` of the decoded polyline. */
export const TrafficSpan = Type.Object(
  {
    start: Type.Integer({ minimum: 0 }),
    end: Type.Integer({ minimum: 0 }),
    speed: Type.Union([Type.Literal("slow"), Type.Literal("jam")]),
  },
  { $id: "TrafficSpan" },
);

/** Route from the driver's position to the trip's current target (pickup, then dropoff). */
export const NavigationRoute = Type.Object(
  {
    target: Type.Union([Type.Literal("pickup"), Type.Literal("stop"), Type.Literal("dropoff")]),
    /** target "stop": which stop (1-based). */
    stopSeq: Type.Optional(Type.Integer({ minimum: 1 })),
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
    polyline: Type.Union([Type.String(), Type.Null()]),
    steps: Type.Array(NavigationStep),
    /** Slow and jammed stretches, drawn over the route; absent when `navigation.traffic_on_route` is off. */
    traffic: Type.Optional(Type.Array(TrafficSpan)),
  },
  { $id: "NavigationRoute" },
);
export type NavigationRoute = Static<typeof NavigationRoute>;

/** The rider as the driver sees them on the trip (Motorista-05, 07, 12, 14). */
export const RiderCard = Type.Object(
  {
    firstName: Type.String(),
    rating: Type.Number(),
    /** Completed trips, next to the rating: "5,0 (200)". */
    completedTrips: Type.Optional(Type.Integer()),
    /** Short-lived link (ADR 0012); absent without a photo: show initials. */
    photoUrl: Type.Optional(Type.String()),
  },
  { $id: "RiderCard" },
);
